<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  rule: {
    type: Object,
    required: true,
  },
});

defineEmits(['edit', 'delete', 'toggle']);

const { t } = useI18n();

const EVENT_ICONS = {
  message_received: 'i-lucide-message-square',
  message_sent: 'i-lucide-send',
  first_response: 'i-lucide-clock',
  stage_changed: 'i-lucide-git-branch',
  card_created: 'i-lucide-plus-square',
  card_assigned: 'i-lucide-user-plus',
  label_added: 'i-lucide-tag',
  label_removed: 'i-lucide-tag-minus',
  conversation_opened: 'i-lucide-message-circle',
  conversation_resolved: 'i-lucide-check-circle',
  conversation_reopened: 'i-lucide-refresh-cw',
  contact_profile_updated: 'i-lucide-user-cog',
  contact_email_added: 'i-lucide-mail',
  contact_phone_added: 'i-lucide-phone',
  agent_assigned: 'i-lucide-user-check',
  agent_replied: 'i-lucide-reply',
};

const eventIconClass = computed(() => {
  return EVENT_ICONS[props.rule.event_type] || 'i-lucide-zap';
});

const eventLabel = computed(() => {
  const key = `LEAD_SCORE.EVENTS.${props.rule.event_type?.toUpperCase()}`;
  return t(key);
});

const eventDescription = computed(() => {
  const key = `LEAD_SCORE.EVENT_DESCRIPTIONS.${props.rule.event_type?.toUpperCase()}`;
  return t(key);
});

const pointsDisplay = computed(() => {
  const points = props.rule.points || 0;
  return points > 0 ? `+${points}` : points;
});

const pointsBadgeClass = computed(() => {
  const points = props.rule.points || 0;
  if (points > 0) {
    return 'bg-n-teal-3 text-n-teal-11 border border-n-teal-6';
  }
  if (points < 0) {
    return 'bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-6';
  }
  return 'bg-n-alpha-2 text-n-slate-11';
});

const pointsColorClass = computed(() => {
  const points = props.rule.points || 0;
  if (points > 0) {
    return 'bg-n-teal-3 text-n-teal-11';
  }
  if (points < 0) {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }
  return 'bg-n-alpha-2 text-n-slate-11';
});

const hasConditions = computed(() => {
  return props.rule.conditions && Object.keys(props.rule.conditions).length > 0;
});

const conditionsCount = computed(() => {
  if (!props.rule.conditions) return 0;
  return Object.keys(props.rule.conditions).length;
});

const formatCooldown = minutes => {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
};
</script>

<template>
  <div
    class="flex items-center justify-between rounded-lg border border-n-weak p-4 hover:bg-n-alpha-1 transition-colors"
    :class="{ 'opacity-50': !rule.enabled }"
  >
    <div class="flex items-center gap-4 flex-1 min-w-0">
      <!-- Icon -->
      <div
        class="size-10 rounded-lg flex items-center justify-center shrink-0"
        :class="pointsColorClass"
      >
        <span :class="eventIconClass" class="size-5" />
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-medium text-n-slate-12 truncate">
            {{ rule.name || eventLabel }}
          </h3>
          <span
            v-if="rule.cooldown_minutes > 0"
            class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
          >
            {{ formatCooldown(rule.cooldown_minutes) }}
          </span>
        </div>
        <p class="text-xs text-n-slate-11 truncate mt-0.5">
          {{ rule.description || eventDescription }}
        </p>
        <div v-if="hasConditions" class="flex items-center gap-1 mt-1">
          <span class="i-lucide-filter size-3 text-n-slate-10" />
          <span class="text-xs text-n-slate-10">
            {{ conditionsCount }} {{ $t('LEAD_SCORE.SETTINGS.CONDITIONS') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Points Badge -->
    <div class="flex items-center gap-3 shrink-0">
      <div
        class="px-3 py-1.5 rounded-lg text-sm font-semibold"
        :class="pointsBadgeClass"
      >
        {{ pointsDisplay }}
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-1">
        <button
          class="p-1.5 rounded-lg hover:bg-n-alpha-2 transition-colors"
          :title="
            rule.enabled
              ? $t('LEAD_SCORE.SETTINGS.DISABLE')
              : $t('LEAD_SCORE.SETTINGS.ENABLE')
          "
          @click="$emit('toggle', rule)"
        >
          <span
            :class="
              rule.enabled
                ? 'i-lucide-toggle-right text-n-brand'
                : 'i-lucide-toggle-left text-n-slate-10'
            "
            class="size-5"
          />
        </button>
        <button
          class="p-1.5 rounded-lg hover:bg-n-alpha-2 transition-colors"
          :title="$t('LEAD_SCORE.SETTINGS.EDIT')"
          @click="$emit('edit', rule)"
        >
          <span class="i-lucide-pencil size-4 text-n-slate-11" />
        </button>
        <button
          class="p-1.5 rounded-lg hover:bg-n-alpha-2 hover:text-ruby-500 transition-colors"
          :title="$t('LEAD_SCORE.SETTINGS.DELETE_RULE')"
          @click="$emit('delete', rule)"
        >
          <span class="i-lucide-trash-2 size-4 text-n-slate-11" />
        </button>
      </div>
    </div>
  </div>
</template>
